<template>
  <v-app>
    <v-main>
      <v-container>
        <v-card>
          <v-card-title class="d-flex justify-space-between align-center">
            <div>{{ $t("users.roles.title") }} — {{ user?.email || "" }}</div>
            <v-btn color="primary" class="text-none" prepend-icon="mdi-plus" @click="openAddDialog">
              {{ $t("users.roles.add") }}
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-alert v-if="user && !user.company_id" type="warning" dense class="mb-4">
              {{ $t("users.roles.noCompanyWarning") }}
            </v-alert>

            <v-skeleton-loader v-if="loadingUser" type="list-item-avatar"></v-skeleton-loader>
            <div v-else class="summary-grid mb-4">
              <div class="summary-block">
                <div class="summary-label">{{ $t("users.fields.name") }}</div>
                <div class="summary-value">{{ user?.name || "—" }}</div>
              </div>
              <div class="summary-block">
                <div class="summary-label">{{ $t("users.fields.email") }}</div>
                <div class="summary-value">{{ user?.email || "—" }}</div>
              </div>
              <div class="summary-block">
                <div class="summary-label">{{ $t("users.fields.role") }}</div>
                <div class="summary-value pill">{{ user?.role || $t("users.roleUndefined") }}</div>
              </div>
              <div class="summary-block">
                <div class="summary-label">{{ $t("users.roles.company") }}</div>
                <div class="summary-value">
                  <span v-if="user?.company_name">{{ user.company_name }}</span>
                  <span v-else>—</span>
                  <span v-if="user?.company_id" class="muted"> (ID {{ user.company_id }})</span>
                </div>
              </div>
            </div>

            <v-divider class="my-4"></v-divider>

            <v-skeleton-loader v-if="rolesLoading" type="table"></v-skeleton-loader>
            <div v-else class="roles-table">
              <v-table density="comfortable">
                <thead>
                  <tr>
                    <th>{{ $t("users.roles.role") }}</th>
                    <th>{{ $t("users.roles.company") }}</th>
                    <th>{{ $t("users.roles.validFrom") }}</th>
                    <th>{{ $t("users.roles.validTo") }}</th>
                    <th>{{ $t("users.roles.disabled") }}</th>
                    <th class="text-right">{{ $t("users.actions") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="userRoles.length === 0">
                    <td colspan="6">{{ $t("users.roles.empty") }}</td>
                  </tr>
                  <tr v-for="role in userRoles" :key="role.id">
                    <td>
                      <v-chip color="primary" label>
                        {{ role.role_key || role.key || role.role || "-" }}
                      </v-chip>
                    </td>
                    <td>
                      <span>{{ displayCompany(role) }}</span>
                    </td>
                    <td>{{ formatDate(role.valid_from) || "—" }}</td>
                    <td>{{ formatDate(role.valid_to) || "—" }}</td>
                    <td>
                      <v-chip
                        :color="role.disabled ? 'warning' : 'success'"
                        label
                      >
                        {{ role.disabled ? $t("app.labels.yes") : $t("app.labels.no") }}
                      </v-chip>
                    </td>
                    <td class="text-right">
                      <v-btn
                        icon
                        size="small"
                        variant="text"
                        @click="toggleDisable(role)"
                        :title="$t('users.roles.disable')"
                      >
                        <v-icon>{{ role.disabled ? "mdi-restore" : "mdi-pause-circle" }}</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        size="small"
                        variant="text"
                        color="error"
                        @click="removeRole(role)"
                        :title="$t('users.roles.remove')"
                      >
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </div>

          </v-card-text>
        </v-card>

        <!-- Dialog adăugare rol -->
        <v-dialog v-model="addDialog" max-width="720px">
          <v-card>
            <v-card-title>{{ $t("users.roles.add") }}</v-card-title>
            <v-card-text>
              <v-form @submit.prevent="addRole">
                <v-row>
                  <v-col cols="12" md="6">
                    <v-autocomplete
                      v-model="newRole.role_key"
                      :items="availableRoleOptions"
                      :label="$t('users.roles.selectRole')"
                      dense
                      outlined
                      clearable
                      :loading="availableRolesLoading"
                      item-title="label"
                      item-value="key"
                      hide-selected
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-autocomplete
                      v-if="requesterIsAdmin"
                      v-model="newRole.company_id"
                      :items="companyOptions"
                      :label="$t('users.roles.company')"
                      dense
                      outlined
                      clearable
                      :loading="companiesLoading"
                      item-title="label"
                      item-value="id"
                      :hint="$t('users.roles.companySelectHint')"
                      persistent-hint
                    ></v-autocomplete>
                    <v-text-field
                      v-else
                      :model-value="lockedCompanyDisplay"
                      :label="$t('users.roles.company')"
                      dense
                      outlined
                      readonly
                      :hint="$t('users.roles.companyLocked')"
                      persistent-hint
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="newRole.valid_from"
                      :label="$t('users.roles.validFrom')"
                      type="date"
                      dense
                      outlined
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="newRole.valid_to"
                      :label="$t('users.roles.validTo')"
                      type="date"
                      dense
                      outlined
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-checkbox
                      v-model="newRole.disabled"
                      :label="$t('users.roles.disabled')"
                      dense
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="12" class="d-flex justify-end">
                    <v-btn variant="text" @click="closeAddDialog">
                      {{ $t("app.actions.cancel") }}
                    </v-btn>
                    <v-btn color="primary" type="submit" class="ml-2">
                      {{ $t("users.roles.add") }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
          </v-card>
        </v-dialog>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import axios from "axios";
import { decodeTokenRoles } from "@/Utile/navigationDrawerAuth.mjs";

export default {
  name: "UserRoles",
  data() {
    return {
      userId: null,
      user: null,
      loadingUser: false,
      userRoles: [],
      rolesLoading: false,
      availableRoles: [],
      availableRolesLoading: false,
      availableCompanies: [],
      companiesLoading: false,
      requesterIsAdmin: false,
      addDialog: false,
      newRole: {
        role_key: "",
        company_id: "",
        valid_from: "",
        valid_to: "",
        disabled: false,
      },
    };
  },
  computed: {
    availableRoleOptions() {
      return this.availableRoles
        .filter((role) => this.requesterIsAdmin || this.normalizeRoleKey(role.key || role.role_key || role.name) !== "miridar")
        .map((r) => ({
          key: r.key || r.role_key || r.name,
          label: r.label_ro || r.label || r.key || r.role_key || r.name,
        }));
    },
    companyOptions() {
      return this.availableCompanies.map((company) => ({
        id: Number(company.id),
        label: `${company.company_name || company.name || this.$t("users.roles.company")} (#${company.id})`,
      }));
    },
    lockedCompanyDisplay() {
      if (this.user?.company_name && this.user?.company_id) {
        return `${this.user.company_name} (ID ${this.user.company_id})`;
      }
      if (this.user?.company_name) return this.user.company_name;
      if (this.user?.company_id) return `ID ${this.user.company_id}`;
      return "—";
    },
  },
  created() {
    this.userId = this.$route.params.id;
    this.resolveRequesterRoleScope();
    this.fetchUser();
    this.fetchAvailableRoles();
    if (this.requesterIsAdmin) this.fetchCompanies();
    this.fetchUserRoles();
  },
  methods: {
    normalizeRoleKey(value) {
      if (!value) return null;
      return String(value).trim().toLowerCase();
    },
    resolveRequesterRoleScope() {
      const rolesSet = new Set();
      try {
        const rawRoles = JSON.parse(localStorage.getItem("user-roles") || "[]");
        if (Array.isArray(rawRoles)) {
          rawRoles.forEach((entry) => {
            if (!entry) return;
            const key = typeof entry === "string" ? entry : entry.key || entry.role || entry.name;
            const normalized = this.normalizeRoleKey(key);
            if (normalized) rolesSet.add(normalized);
          });
        }
      } catch (_) {
        // No-op: fallback pe user-role legacy.
      }
      decodeTokenRoles().forEach((roleKey) => {
        const normalized = this.normalizeRoleKey(roleKey);
        if (normalized) rolesSet.add(normalized);
      });
      const legacyRole = this.normalizeRoleKey(localStorage.getItem("user-role"));
      if (legacyRole) rolesSet.add(legacyRole);
      this.requesterIsAdmin = rolesSet.has("admin");
    },
    normalizeCompanyId(value) {
      if (value === "" || value === null || value === undefined) return null;
      const numeric = Number(value);
      return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
    },
    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return "";
      const offset = date.getTimezoneOffset();
      date.setMinutes(date.getMinutes() - offset);
      return date.toISOString().split("T")[0];
    },
    resetNewRole() {
      this.newRole = {
        role_key: "",
        company_id: this.normalizeCompanyId(this.user?.company_id),
        valid_from: "",
        valid_to: "",
        disabled: false,
      };
    },
    openAddDialog() {
      this.resetNewRole();
      this.addDialog = true;
    },
    closeAddDialog() {
      this.addDialog = false;
    },
    fetchUser() {
      if (!this.userId) return;
      this.loadingUser = true;
      axios
        .get(`/users/${this.userId}`)
        .then((response) => {
          this.user = response.data;
          if (!this.addDialog) {
            this.newRole.company_id = this.normalizeCompanyId(this.user?.company_id);
          }
        })
        .catch((error) => {
          console.error("Eroare la încărcarea utilizatorului:", error);
        })
        .finally(() => {
          this.loadingUser = false;
        });
    },
    fetchAvailableRoles() {
      this.availableRolesLoading = true;
      axios
        .get("/roles")
        .then((response) => {
          this.availableRoles = Array.isArray(response.data) ? response.data : [];
        })
        .catch((error) => {
          console.error("Eroare la listarea rolurilor:", error);
          this.availableRoles = [];
        })
        .finally(() => {
          this.availableRolesLoading = false;
        });
    },
    fetchCompanies() {
      this.companiesLoading = true;
      axios
        .get("/candidati/companies")
        .then((response) => {
          this.availableCompanies = Array.isArray(response.data) ? response.data : [];
        })
        .catch((error) => {
          console.error("Eroare la listarea companiilor:", error);
          this.availableCompanies = [];
        })
        .finally(() => {
          this.companiesLoading = false;
        });
    },
    fetchUserRoles() {
      if (!this.userId) return;
      this.rolesLoading = true;
      axios
        .get(`/users/${this.userId}/roles`)
        .then((response) => {
          const roles = Array.isArray(response.data) ? response.data : [];
          this.userRoles = roles.filter((role) => this.requesterIsAdmin || this.normalizeRoleKey(role.role_key) !== "miridar");
        })
        .catch((error) => {
          console.error("Eroare la listarea rolurilor utilizatorului:", error);
          this.userRoles = [];
          alert(this.$t("users.roles.loadError"));
        })
        .finally(() => {
          this.rolesLoading = false;
        });
    },
    displayCompany(role) {
      if (role.company_name) return role.company_name;
      if (role.company_id) return `ID: ${role.company_id}`;
      return "—";
    },
    addRole() {
      if (!this.userId || !this.newRole.role_key) {
        alert(this.$t("users.roles.missingRole"));
        return;
      }
      const payload = {
        role_key: this.newRole.role_key,
        company_id: this.normalizeCompanyId(this.newRole.company_id),
        valid_from: this.newRole.valid_from || null,
        valid_to: this.newRole.valid_to || null,
        disabled: this.newRole.disabled ? 1 : 0,
      };
      axios
        .post(`/users/${this.userId}/roles`, payload)
        .then(() => {
          this.fetchUserRoles();
          this.closeAddDialog();
          this.resetNewRole();
        })
        .catch((error) => {
          console.error("Eroare la adăugarea rolului:", error);
          const backendMessage = error?.response?.data?.message || error?.response?.data?.error;
          alert(backendMessage || this.$t("users.roles.addError"));
        });
    },
    toggleDisable(role) {
      if (!this.userId || !role.id) return;
      // prevenim dezactivarea ultimei asignări
      if (!role.disabled && this.userRoles.filter((r) => !r.disabled).length <= 1) {
        alert(this.$t("users.roles.lastRoleError") || "Nu poți dezactiva ultimul rol.");
        return;
      }
      const payload = { disabled: role.disabled ? 0 : 1 };
      axios
        .put(`/users/${this.userId}/roles/${role.id}`, payload)
        .then(() => {
          this.fetchUserRoles();
        })
        .catch((error) => {
          console.error("Eroare la actualizarea rolului:", error);
          alert(this.$t("users.roles.updateError"));
        });
    },
    removeRole(role) {
      if (!this.userId || !role.id) return;
      if (this.userRoles.length <= 1) {
        alert(this.$t("users.roles.lastRoleError") || "Nu poți șterge ultimul rol.");
        return;
      }
      axios
        .delete(`/users/${this.userId}/roles/${role.id}`)
        .then(() => {
          this.fetchUserRoles();
        })
        .catch((error) => {
          console.error("Eroare la ștergerea rolului:", error);
          alert(this.$t("users.roles.deleteError"));
        });
    },
  },
};
</script>

<style scoped>
.mb-4 {
  margin-bottom: 16px;
}
.user-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
}
.summary-item,
.summary-block {
  display: flex;
  flex-direction: column;
  padding: 8px 0;
}
.summary-item .label {
  font-size: 12px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.summary-item .value,
.summary-block .summary-value {
  font-weight: 600;
}
.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
}
.summary-label {
  font-size: 12px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.summary-value {
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
}
.summary-value.pill {
  display: inline-flex;
  padding: 4px 8px;
  border-radius: 999px;
  background: rgba(34, 61, 77, 0.08);
}
.roles-table table {
  width: 100%;
}
.roles-table th,
.roles-table td {
  padding: 10px 8px;
}
</style>
